import { useEffect, useState } from "react";
import { FileText, Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiErrorDetail, apiErrorCode } from "@/lib/api";
import { DOCUMENT_GENERATE_DIALOG } from "@/constants/testIds";
import { toast } from "sonner";

export function DocumentGenerateDialog({ template, employees, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [fields, setFields] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState(null);

  const needsEmployee = template.scope === "employee";

  const loadDraft = (empId) => {
    if (needsEmployee && !empId) return;
    setLoadingDraft(true);
    api
      .get(`/document-templates/${template.id}/draft`, { params: empId ? { employee_id: empId } : {} })
      .then(({ data }) => {
        if (template.is_editable_draft) {
          setDraftBody(data.body_markdown);
        } else {
          setFields(data.field_defaults || {});
        }
      })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoadingDraft(false));
  };

  const handleOpen = (v) => {
    setOpen(v);
    if (v) {
      setEmployeeId("");
      setDraftBody("");
      setFields({});
      setConflict(null);
      if (!needsEmployee) loadDraft(null);
    }
  };

  const handleEmployeeChange = (val) => {
    setEmployeeId(val);
    setConflict(null);
    loadDraft(val);
  };

  const buildPayload = (replace) => {
    const payload = { employee_id: employeeId || null, replace };
    if (template.is_editable_draft) {
      payload.override_body = draftBody;
    } else {
      payload.extra_fields = fields;
    }
    return payload;
  };

  const handleSubmit = async (replace = false) => {
    if (needsEmployee && !employeeId) {
      toast.error("Selecciona un empleado");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/document-templates/${template.id}/generate`, buildPayload(replace));
      toast.success(data.message);
      setOpen(false);
      setConflict(null);
      onGenerated?.();
    } catch (err) {
      if (apiErrorCode(err) === "DOCUMENT_ALREADY_EXISTS") {
        setConflict(err.response.data.detail);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const showForm = !needsEmployee || employeeId;

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={`${DOCUMENT_GENERATE_DIALOG.dialog}-trigger-${template.id}`}
          size="sm"
          className="bg-petroleum hover:bg-petroleum/90 rounded-full shrink-0"
        >
          <FileText className="w-4 h-4 mr-1.5" /> Generar
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={DOCUMENT_GENERATE_DIALOG.dialog} className="bg-bone max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{template.name}</DialogTitle>
          <DialogDescription>{template.description}</DialogDescription>
        </DialogHeader>

        {needsEmployee && (
          <div className="mb-2">
            <Label>Empleado</Label>
            <Select data-testid={DOCUMENT_GENERATE_DIALOG.employeeSelect} value={employeeId} onValueChange={handleEmployeeChange}>
              <SelectTrigger className="bg-white"><SelectValue placeholder="Selecciona un empleado" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {loadingDraft && (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-petroleum animate-spin" /></div>
        )}

        {!loadingDraft && showForm && template.is_editable_draft && (
          <div>
            <Label>Texto del reglamento (edítalo antes de generar)</Label>
            <Textarea
              data-testid={DOCUMENT_GENERATE_DIALOG.draftTextarea}
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              className="bg-white min-h-[40vh] text-xs font-mono-legal"
            />
          </div>
        )}

        {!loadingDraft && showForm && !template.is_editable_draft && (
          <div className="space-y-3">
            {template.extra_fields.map((f) => (
              <div key={f.key}>
                <Label>{f.label}{f.required && " *"}</Label>
                {f.type === "textarea" ? (
                  <Textarea
                    data-testid={`${DOCUMENT_GENERATE_DIALOG.fieldInputPrefix}-${f.key}`}
                    value={fields[f.key] ?? ""}
                    placeholder={f.placeholder || `Escribe: ${f.label.toLowerCase()}`}
                    onChange={(e) => setFields((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    className="bg-white"
                  />
                ) : f.type === "select" ? (
                  <Select
                    value={fields[f.key] ?? ""}
                    onValueChange={(val) => setFields((prev) => ({ ...prev, [f.key]: val }))}
                  >
                    <SelectTrigger data-testid={`${DOCUMENT_GENERATE_DIALOG.fieldInputPrefix}-${f.key}`} className="bg-white">
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent>
                      {(f.options || []).map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    data-testid={`${DOCUMENT_GENERATE_DIALOG.fieldInputPrefix}-${f.key}`}
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={fields[f.key] ?? ""}
                    placeholder={f.placeholder || (f.type === "date" ? undefined : `Escribe: ${f.label.toLowerCase()}`)}
                    onChange={(e) => setFields((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    className="bg-white"
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {conflict && (
          <div data-testid={DOCUMENT_GENERATE_DIALOG.conflictWarning} className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="text-xs text-amber-800">{conflict.message}</p>
              <Button
                data-testid={DOCUMENT_GENERATE_DIALOG.confirmReplaceButton}
                size="sm" variant="outline" disabled={submitting}
                onClick={() => handleSubmit(true)}
                className="rounded-full border-amber-400 text-amber-800 h-7 text-xs"
              >
                Reemplazar versión anterior y generar esta
              </Button>
            </div>
          </div>
        )}

        {showForm && (
          <Button
            data-testid={DOCUMENT_GENERATE_DIALOG.submitButton}
            onClick={() => handleSubmit(false)}
            disabled={submitting || loadingDraft}
            className="w-full bg-terracotta hover:bg-terracotta/90 rounded-full mt-2"
          >
            {submitting ? "Generando..." : "Generar documento"}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
